import React, { useState, useEffect } from 'react';
import merchantKycService from '../services/merchantKycService';
import KYCVerificationCenter from './KYCVerificationCenter';
import './MerchantKycVerification.css';

const MerchantKycVerification = ({ merchantProfile, onProfileUpdate }) => {
  const [verificationData, setVerificationData] = useState(null);
  const [canStartVerification, setCanStartVerification] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkVerificationEligibility();
  }, [merchantProfile]);

  const checkVerificationEligibility = () => {
    try {
      // Check if profile is complete enough for verification
      const profile = merchantProfile;
      const documents = merchantKycService.getMerchantDocuments(profile.merchantId);
      const verification = merchantKycService.getMerchantVerification(profile.merchantId);

      console.log('🔍 MerchantKycVerification: Checking eligibility', {
        profile: profile.kycStatus,
        documentsCount: documents.documents.length,
        hasVerification: !!verification,
        verificationResults: verification?.results
      });

      // Check minimum requirements
      const hasBasicInfo = profile.personalInfo.fullName && 
                          profile.personalInfo.email && 
                          profile.businessInfo.businessName;
      
      const hasRequiredDocs = documents.documents.length >= 2; // At least 2 documents
      
      setCanStartVerification(hasBasicInfo && hasRequiredDocs);
      setVerificationData(verification);
    } catch (error) {
      console.error('Error checking verification eligibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = (results) => {
    console.log('🔍 MerchantKycVerification: Handling verification completion', results);
    
    // Save verification results using merchant service
    const savedVerification = merchantKycService.saveMerchantVerification(
      merchantProfile.merchantId, 
      results
    );

    if (savedVerification) {
      setVerificationData(savedVerification);
      
      // Recalculate completion percentage after verification
      const newCompletionPercentage = merchantKycService.calculateCompletionPercentage(merchantProfile.merchantId);
      
      console.log('✅ Verification completed and saved:', {
        merchantId: merchantProfile.merchantId,
        status: results.status,
        completionPercentage: newCompletionPercentage,
        hasResults: !!savedVerification.results
      });
      
      // Update profile status
      if (onProfileUpdate) {
        const updatedProfile = {
          ...merchantProfile,
          kycStatus: {
            ...merchantProfile.kycStatus,
            status: results.status || 'PENDING_REVIEW',
            completionPercentage: newCompletionPercentage,
            submittedAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          }
        };
        
        // If approved, ensure 100% completion
        if (results.status === 'APPROVED') {
          updatedProfile.kycStatus.completionPercentage = 100;
          updatedProfile.kycStatus.approvedAt = new Date().toISOString();
        }
        
        onProfileUpdate(updatedProfile);
      }

      // Dispatch events to notify other components
      window.dispatchEvent(new CustomEvent('kycVerificationCompleted', {
        detail: { 
          merchantId: merchantProfile.merchantId, 
          results, 
          completionPercentage: newCompletionPercentage,
          hasVerificationResults: true
        }
      }));

      window.dispatchEvent(new CustomEvent('kycStatusUpdated', {
        detail: { 
          merchantId: merchantProfile.merchantId, 
          status: results.status,
          completionPercentage: newCompletionPercentage
        }
      }));

      // Show success notification
      if (window.showNotification) {
        window.showNotification('KYC verification completed successfully!', 'success');
      }
    } else {
      console.error('❌ Failed to save verification results');
      if (window.showNotification) {
        window.showNotification('Failed to save verification results. Please try again.', 'error');
      }
    }
  };

  const resetVerification = () => {
    if (window.confirm('Are you sure you want to reset the verification? This will clear all verification results.')) {
      // Clear verification data
      localStorage.removeItem(`merchant_verification_${merchantProfile.merchantId}`);
      setVerificationData(null);
      
      // Update profile status
      merchantKycService.updateMerchantKycStatus(merchantProfile.merchantId, {
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString()
      });

      if (window.showNotification) {
        window.showNotification('Verification data has been reset', 'info');
      }
    }
  };

  const getVerificationStatusInfo = (status) => {
    const statusMap = {
      'APPROVED': {
        color: '#28a745',
        icon: '✅',
        title: 'Verification Approved',
        description: 'Your KYC verification has been successfully completed and approved.'
      },
      'PENDING_REVIEW': {
        color: '#17a2b8',
        icon: '👀',
        title: 'Under Review',
        description: 'Your verification is being reviewed by our team. This usually takes 24-48 hours.'
      },
      'REJECTED': {
        color: '#dc3545',
        icon: '❌',
        title: 'Verification Rejected',
        description: 'Your verification was rejected. Please review the feedback and resubmit.'
      },
      'RESUBMISSION_REQUIRED': {
        color: '#fd7e14',
        icon: '🔄',
        title: 'Resubmission Required',
        description: 'Some documents need to be updated. Please make the required changes and resubmit.'
      }
    };

    return statusMap[status] || null;
  };

  if (loading) {
    return (
      <div className="merchant-kyc-verification">
        <div className="loading-spinner">Loading verification status...</div>
      </div>
    );
  }

  // If verification is already completed, show results
  if (verificationData && verificationData.results) {
    const statusInfo = getVerificationStatusInfo(verificationData.results.status);
    
    return (
      <div className="merchant-kyc-verification">
        <div className="verification-completed">
          {statusInfo && (
            <div className="status-card" style={{ borderColor: statusInfo.color }}>
              <div className="status-header">
                <div className="status-icon" style={{ color: statusInfo.color }}>
                  {statusInfo.icon}
                </div>
                <div className="status-info">
                  <h2 style={{ color: statusInfo.color }}>{statusInfo.title}</h2>
                  <p>{statusInfo.description}</p>
                </div>
              </div>
            </div>
          )}

          <div className="verification-results">
            <h3>📊 Verification Results</h3>
            <div className="results-grid">
              <div className="result-card">
                <h3>Overall Score</h3>
                <div className="score-display">
                  <span className="score-value">{verificationData.results.finalScore || 0}%</span>
                  <div className="score-bar">
                    <div 
                      className="score-fill" 
                      style={{ 
                        width: `${verificationData.results.finalScore || 0}%`,
                        backgroundColor: statusInfo?.color || '#6c757d'
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {verificationData.results.stepScores && (
                <div className="step-scores">
                  <h3>Step-wise Scores</h3>
                  {Object.entries(verificationData.results.stepScores).map(([step, score]) => (
                    <div key={step} className="step-score">
                      <span className="step-name">{step.replace(/_/g, ' ')}</span>
                      <span className="step-value">{score}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {verificationData.results.summary && (
              <div className="verification-summary">
                <h4>📝 Summary</h4>
                <p>{verificationData.results.summary}</p>
              </div>
            )}

            <div className="verification-actions">
              <button 
                className="btn btn-outline"
                onClick={resetVerification}
              >
                🔄 Reset Verification (Demo)
              </button>
              
              {verificationData.results.status === 'APPROVED' && (
                <button 
                  className="btn btn-success"
                  onClick={() => window.location.href = '/dashboard'}
                >
                  🎉 Go to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If not eligible for verification, show requirements
  if (!canStartVerification) {
    return (
      <div className="merchant-kyc-verification">
        <div className="verification-requirements">
          <div className="requirements-header">
            <h2>🔍 KYC Verification</h2>
            <p>Complete the following requirements before starting verification:</p>
          </div>

          <div className="requirements-list">
            <div className={`requirement-item ${merchantProfile.personalInfo.fullName ? 'completed' : ''}`}>
              <span className="requirement-icon">
                {merchantProfile.personalInfo.fullName ? '✅' : '⭕'}
              </span>
              <div className="requirement-content">
                <h3>Personal Information</h3>
                <p>Complete your personal details including name and email</p>
              </div>
            </div>

            <div className={`requirement-item ${merchantProfile.businessInfo.businessName ? 'completed' : ''}`}>
              <span className="requirement-icon">
                {merchantProfile.businessInfo.businessName ? '✅' : '⭕'}
              </span>
              <div className="requirement-content">
                <h3>Business Information</h3>
                <p>Provide complete business details and address</p>
              </div>
            </div>

            <div className={`requirement-item ${merchantKycService.getMerchantDocuments(merchantProfile.merchantId).documents.length >= 2 ? 'completed' : ''}`}>
              <span className="requirement-icon">
                {merchantKycService.getMerchantDocuments(merchantProfile.merchantId).documents.length >= 2 ? '✅' : '⭕'}
              </span>
              <div className="requirement-content">
                <h3>Document Upload</h3>
                <p>Upload at least 2 required documents (PAN, Aadhaar, etc.)</p>
                <small>Currently uploaded: {merchantKycService.getMerchantDocuments(merchantProfile.merchantId).documents.length} documents</small>
              </div>
            </div>
          </div>

          <div className="requirements-actions">
            <p>Complete the above requirements to start your KYC verification process.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show verification center
  return (
    <div className="merchant-kyc-verification">
      <div className="verification-intro">
        <h2>🔍 Start KYC Verification</h2>
        <p>Your profile and documents are ready for verification. This process will analyze your submitted information and documents.</p>
      </div>

      <KYCVerificationCenter 
        onVerificationComplete={handleVerificationComplete}
        merchantId={merchantProfile.merchantId}
      />
    </div>
  );
};

export default MerchantKycVerification;